import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { checkoutAuthHeaders } from '../checkout/checkoutAuthHeaders';
import { isOrderCancelled, orderStatusDisplay } from './orderStatusLabel';
import { findProductInCatalog } from '../catalog/findProduct';
import CustomerShipmentTracking from './CustomerShipmentTracking';
import { CustomerOrderPaymentSection } from './orderPaymentDisplay';
import AccountOrderReturnRequest from './AccountOrderReturnRequest';

function formatMoney(n) {
  return `\u20B9${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const ORDER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'cancelled', label: 'Cancelled' },
];

function AccountOrderDetailBody({ detail, onReorder, onRequestCancel, onOrderUpdated, setReturnFeedback }) {
  const status = String(detail.status || '').toLowerCase();
  const isCancelled = isOrderCancelled(detail);
  const discount = Number(detail.discount_amount || 0);
  const items = detail.items || [];

  return (
    <>
      {isCancelled ? (
        <div className="alert alert-secondary py-2 small mb-2" role="status">
          This order was cancelled. Items and totals are shown below for your records.
        </div>
      ) : null}
      {!isCancelled && detail.cancel_requested_at ? (
        <div className="alert alert-warning py-2 small mb-2" role="status">
          Cancellation requested — we will confirm by email or WhatsApp.
        </div>
      ) : null}
      <CustomerOrderPaymentSection order={detail} />
      {detail.shipping ? (
        <section className="account-order-detail-section account-order-detail-section--shipping mb-2">
          <p className="account-order-detail-section__label">Delivery address</p>
          <p className="account-order-detail-section__line mb-0">
            <strong>{detail.shipping.name}</strong>
            {detail.shipping.phone ? (
              <span className="text-muted"> · {detail.shipping.phone}</span>
            ) : null}
          </p>
          <p className="account-order-detail-section__line small text-muted mb-0">
            {detail.shipping.address}
          </p>
          <p className="account-order-detail-section__line small text-muted mb-0">
            {detail.shipping.city}
            {detail.shipping.state ? `, ${detail.shipping.state}` : ''} {detail.shipping.pincode}
          </p>
        </section>
      ) : null}
      {Array.isArray(detail.shipments) && detail.shipments.length > 0 ? (
        <div className="customer-order-shipments mb-2">
          <p className="small fw-semibold mb-2">Shipments</p>
          <ul className="list-unstyled mb-0">
            {detail.shipments.map((s) => (
              <li
                key={s.id || s.shipment_seq}
                className="customer-order-shipments__item alert alert-light border small py-3 mb-2"
              >
                <p className="small fw-semibold mb-2">
                  Package {s.shipment_seq}
                  {s.shipped_at ? (
                    <span className="fw-normal text-muted">
                      {' '}
                      · {new Date(s.shipped_at).toLocaleString('en-IN')}
                    </span>
                  ) : null}
                </p>
                {(s.items || []).length > 0 ? (
                  <ul className="small text-muted mb-2 ps-3">
                    {s.items.map((si, i) => (
                      <li key={`${si.order_item_id}-${i}`}>
                        {si.product_name} × {si.quantity}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <CustomerShipmentTracking
                  compact={false}
                  tracking_number={s.tracking_number}
                  tracking_url={s.tracking_url}
                  tracking_carrier_name={s.tracking_carrier_name}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : detail.tracking_number || detail.tracking_url ? (
        <CustomerShipmentTracking
          className="alert alert-light border small py-3 mb-2"
          tracking_number={detail.tracking_number}
          tracking_url={detail.tracking_url}
          tracking_carrier_name={detail.tracking_carrier_name}
        />
      ) : null}
      <div className="account-order-summary">
        <p className="account-order-summary__title small fw-semibold mb-2">Order summary</p>
        {items.length > 0 ? (
          <ul className="account-order-summary__lines list-unstyled small mb-2">
            {items.map((it, idx) => (
              <li key={`${it.product_external_id}-${idx}`} className="account-order-summary__line">
                <span>
                  {it.product_name} {'\u00D7'} {it.quantity}
                  {it.customization_text ? (
                    <span className="d-block text-muted">&ldquo;{it.customization_text}&rdquo;</span>
                  ) : null}
                </span>
                <span>{formatMoney(it.line_total)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="small text-muted mb-2">No line items on file.</p>
        )}
        <div className="account-order-summary__rows small">
          <div className="account-order-summary__row">
            <span>Subtotal</span>
            <span>{formatMoney(detail.subtotal)}</span>
          </div>
          {Number(detail.shipping_amount) > 0 ? (
            <div className="account-order-summary__row">
              <span>Delivery</span>
              <span>{formatMoney(detail.shipping_amount)}</span>
            </div>
          ) : null}
          {discount > 0 ? (
            <div className="account-order-summary__row text-success">
              <span>Discount{detail.coupon_code ? ` (${detail.coupon_code})` : ''}</span>
              <span>-{formatMoney(discount)}</span>
            </div>
          ) : null}
          <div className="account-order-summary__row account-order-summary__row--total">
            <span>Total</span>
            <span>{formatMoney(detail.total_amount)}</span>
          </div>
        </div>
      </div>

      <AccountOrderReturnRequest
        detail={detail}
        onOrderUpdated={onOrderUpdated}
        setFeedback={setReturnFeedback}
      />

      <div className="d-flex flex-wrap gap-2">
        {!isCancelled ? (
          <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={() => onReorder(detail)}>
            Buy again
          </button>
        ) : null}
        <Link
          to={`/account/invoice/${encodeURIComponent(detail.order_number)}`}
          className="btn btn-outline-secondary btn-sm rounded-pill"
        >
          Print receipt
        </Link>
        {!detail.cancel_requested_at &&
        !isCancelled &&
        status !== 'shipped' &&
        status !== 'completed' ? (
          <button
            type="button"
            className="btn btn-outline-danger btn-sm rounded-pill"
            onClick={() => onRequestCancel(detail.order_number)}
          >
            Request cancel
          </button>
        ) : null}
      </div>
    </>
  );
}

/** Compact order list for account overview (includes cancelled). */
export function AccountOrdersOverviewList({ orders, loading, onViewAll }) {
  if (loading) {
    return <p className="small text-muted mb-0">Loading recent orders…</p>;
  }
  if (!orders.length) {
    return (
      <p className="small text-muted mb-0">
        No orders yet.{' '}
        <Link to="/shop" className="account-link">
          Browse the shop
        </Link>
      </p>
    );
  }
  return (
    <ul className="account-orders-overview list-unstyled mb-0">
      {orders.slice(0, 5).map((o) => {
        const status = orderStatusDisplay(o);
        return (
          <li key={o.order_number} className="account-orders-overview__item">
            <span className="account-orders-overview__id">#{o.order_number}</span>
            <span className={status.className}>{status.label}</span>
            <span className="account-orders-overview__amount">{formatMoney(o.total_amount)}</span>
            <span className="account-orders-overview__date text-muted small">
              {o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN') : ''}
            </span>
          </li>
        );
      })}
      {orders.length > 5 && onViewAll ? (
        <li className="account-orders-overview__more">
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onViewAll}>
            View all {orders.length} orders
          </button>
        </li>
      ) : null}
    </ul>
  );
}

function formatAddressBlock(shipping) {
  if (!shipping) return null;
  const lines = [];
  const name = String(shipping.name || '').trim();
  const phone = String(shipping.phone || '').trim();
  const address = String(shipping.address || '').trim();
  const city = String(shipping.city || '').trim();
  const state = String(shipping.state || '').trim();
  const pincode = String(shipping.pincode || '').trim();
  if (name) lines.push({ key: 'name', text: name });
  if (phone) lines.push({ key: 'phone', text: phone });
  if (address) lines.push({ key: 'address', text: address });
  const cityLine = [city, state].filter(Boolean).join(', ');
  const pinLine = pincode ? (cityLine ? `${cityLine} — ${pincode}` : pincode) : cityLine;
  if (pinLine) lines.push({ key: 'city', text: pinLine });
  return lines.length ? lines : null;
}

export function AccountCustomerDeliveryAddress() {
  const [shipping, setShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/checkout_config.php', { headers: checkoutAuthHeaders() })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d?.ok && d.saved_shipping && typeof d.saved_shipping === 'object') {
          setShipping(d.saved_shipping);
          setError('');
        } else {
          setShipping(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your delivery address.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lines = formatAddressBlock(shipping);

  return (
    <section className="account-password" aria-labelledby="account-address-heading">
      <h2 id="account-address-heading" className="account-section-label">
        Delivery address
      </h2>
      <div className="account-password-card account-address-card">
        {loading ? (
          <p className="small text-muted mb-0">Loading address…</p>
        ) : error ? (
          <p className="small text-danger mb-0">{error}</p>
        ) : !lines ? (
          <>
            <p className="small text-muted mb-2">
              No saved address yet. After your first order, we will show your delivery address here and use it at
              checkout.
            </p>
            <Link to="/shop" className="btn btn-outline-primary btn-sm rounded-pill">
              Browse shop
            </Link>
          </>
        ) : (
          <>
            <p className="account-address-card__badge small mb-2">Default for checkout</p>
            <address className="account-address-block mb-3">
              {lines.map((line) => (
                <span key={line.key} className="account-address-block__line d-block">
                  {line.text}
                </span>
              ))}
            </address>
            <p className="small text-muted mb-2">
              This is taken from your most recent order. To change it, enter your new details on the checkout page
              when you place your next order.
            </p>
            <Link to="/cart" className="btn btn-primary btn-sm rounded-pill">
              Go to bag
            </Link>
          </>
        )}
      </div>
    </section>
  );
}

export function AccountCustomerProfile({ displayName, email, phone, updateProfile, embedded = false }) {
  const [nameDraft, setNameDraft] = useState(displayName || '');
  const [emailDraft, setEmailDraft] = useState(email || '');
  const [phoneDraft, setPhoneDraft] = useState(phone || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    setNameDraft(displayName || '');
    setEmailDraft(email || '');
    setPhoneDraft(phone || '');
  }, [displayName, email, phone]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setErr('');
    const res = await updateProfile(nameDraft, emailDraft, phoneDraft);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error || 'Could not save profile.');
      return;
    }
    setMsg('Profile updated.');
  };

  const body = (
        <form onSubmit={onSubmit} className="customer-account__form">
          <div className="checkout-field">
            <label className="checkout-field__label" htmlFor="account-display-name">
              Display name
            </label>
            <input
              id="account-display-name"
              type="text"
              className="form-control checkout-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              autoComplete="name"
            />
          </div>
          <div className="checkout-field">
            <label className="checkout-field__label" htmlFor="account-email">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              className="form-control checkout-input"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="checkout-field">
            <label className="checkout-field__label" htmlFor="account-phone">
              Phone
            </label>
            <input
              id="account-phone"
              type="tel"
              className="form-control checkout-input"
              value={phoneDraft}
              onChange={(e) => setPhoneDraft(e.target.value)}
              autoComplete="tel"
            />
          </div>
          {err ? <p className="small text-danger">{err}</p> : null}
          {msg ? <p className="small text-success">{msg}</p> : null}
          <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={busy}>
            {busy ? 'Saving…' : 'Save profile'}
          </button>
        </form>
  );

  if (embedded) return body;

  return (
    <section className="account-password" aria-labelledby="account-profile-heading">
      <h2 id="account-profile-heading" className="account-section-label">
        Profile
      </h2>
      <div className="account-password-card">{body}</div>
    </section>
  );
}

export function AccountCustomerOrders({ listOrders, getOrderDetail, addItem, products, embedded = false }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [returnFeedback, setReturnFeedback] = useState(null);
  const expandedRef = useRef(null);

  const reloadOrders = useCallback(async () => {
    const res = await listOrders();
    if (res.ok) {
      setError('');
      setOrders(res.orders);
    } else {
      setError(res.error || 'Could not load orders.');
      setOrders([]);
    }
    return res;
  }, [listOrders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await listOrders();
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || 'Could not load orders.');
        setOrders([]);
      } else {
        setError('');
        setOrders(res.orders);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [listOrders]);

  const filteredOrders = useMemo(() => {
    if (filter === 'cancelled') return orders.filter((o) => isOrderCancelled(o));
    if (filter === 'active') return orders.filter((o) => !isOrderCancelled(o));
    return orders;
  }, [orders, filter]);

  const cancelledCount = useMemo(() => orders.filter((o) => isOrderCancelled(o)).length, [orders]);

  const toggleOrder = async (orderNumber) => {
    if (expandedRef.current === orderNumber) {
      expandedRef.current = null;
      setExpanded(null);
      setDetail(null);
      setDetailError('');
      setReturnFeedback(null);
      return;
    }
    expandedRef.current = orderNumber;
    setExpanded(orderNumber);
    setDetail(null);
    setDetailError('');
    setReturnFeedback(null);
    if (!getOrderDetail) return;
    setDetailLoading(true);
    const res = await getOrderDetail(orderNumber);
    if (expandedRef.current !== orderNumber) return;
    setDetailLoading(false);
    if (!res.ok) {
      setDetailError(res.error || 'Could not load order details.');
      return;
    }
    setDetail(res.order);
  };

  const requestCancel = async (orderNumber) => {
    if (!window.confirm('Request cancellation for this order?')) return;
    try {
      const r = await fetch('/api/orders.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...checkoutAuthHeaders() },
        body: JSON.stringify({ action: 'request_cancel', order_number: orderNumber }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        window.alert(d.error || 'Could not submit request.');
        return;
      }
      window.alert(d.message || 'Cancellation requested.');
      await reloadOrders();
      if (expandedRef.current === orderNumber && getOrderDetail) {
        const det = await getOrderDetail(orderNumber);
        if (det.ok && expandedRef.current === orderNumber) setDetail(det.order);
      }
    } catch {
      window.alert('Could not submit cancellation request.');
    }
  };

  const reorder = (order) => {
    if (!addItem || !products) return;
    let added = 0;
    for (const it of order.items || []) {
      const p = findProductInCatalog(products, it.category_slug, it.product_external_id);
      if (!p) continue;
      const res = addItem(p, it.category_slug, it.quantity);
      if (res?.ok) added += 1;
    }
    if (added > 0) navigate('/cart');
    else window.alert('Some items are no longer available.');
  };

  const panel = (
      <div className={embedded ? 'account-orders-panel account-orders-panel--embedded' : 'account-password-card account-orders-panel'}>
        {loading ? (
          <p className="small text-muted mb-0">Loading orders…</p>
        ) : error ? (
          <p className="small text-danger mb-0">{error}</p>
        ) : orders.length === 0 ? (
          <div className="account-orders-empty">
            <p className="account-orders-empty__title">No orders yet</p>
            <p className="account-orders-empty__text small text-muted mb-3">
              When you place an order, it will appear here with tracking and receipts.
            </p>
            <Link to="/shop" className="btn btn-primary btn-sm rounded-pill">
              Browse the shop
            </Link>
          </div>
        ) : (
          <>
            <div className="account-orders-filters" role="tablist" aria-label="Filter orders">
              {ORDER_FILTERS.map((f) => {
                const count =
                  f.id === 'all'
                    ? orders.length
                    : f.id === 'cancelled'
                      ? cancelledCount
                      : orders.length - cancelledCount;
                return (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.id}
                    className={`account-orders-filters__btn${filter === f.id ? ' is-active' : ''}`}
                    onClick={() => setFilter(f.id)}
                  >
                    {f.label}
                    {count > 0 ? <span className="account-orders-filters__count">{count}</span> : null}
                  </button>
                );
              })}
            </div>
            {filteredOrders.length === 0 ? (
              <p className="small text-muted mb-0">
                {filter === 'cancelled'
                  ? 'No cancelled orders.'
                  : filter === 'active'
                    ? 'No active orders — check Cancelled for past orders.'
                    : 'No orders match this filter.'}
              </p>
            ) : (
          <ul className="list-unstyled mb-0 account-orders-list">
            {filteredOrders.map((o) => {
              const isOpen = expanded === o.order_number;
              const status = orderStatusDisplay(o);
              return (
                <li
                  key={o.order_number}
                  className={`account-orders-list__item account-order-card${isOrderCancelled(o) ? ' account-order-card--cancelled' : ''}`}
                >
                  <button
                    type="button"
                    className="account-orders-list__toggle account-order-card__toggle"
                    onClick={() => toggleOrder(o.order_number)}
                    aria-expanded={isOpen}
                  >
                    <strong className="account-order-card__id">#{o.order_number}</strong>
                    <span className="account-order-card__date text-muted small d-block">
                      {o.created_at ? new Date(o.created_at).toLocaleString('en-IN') : ''}
                    </span>
                    {o.tracking_number || o.tracking_url ? (
                      <CustomerShipmentTracking
                        compact
                        className="account-order-card__note small d-block text-primary"
                        tracking_number={o.tracking_number}
                        tracking_url={o.tracking_url}
                        tracking_carrier_name={o.tracking_carrier_name}
                      />
                    ) : null}
                    {o.cancel_requested_at ? (
                      <span className="account-order-card__note small d-block text-warning">Cancel requested</span>
                    ) : null}
                    <div className="account-order-card__footer">
                      <span className={status.className}>{status.label}</span>
                      <span className="account-order-card__amount">{formatMoney(o.total_amount)}</span>
                      <span className="account-order-card__details small text-muted">
                        {isOpen ? 'Hide details' : 'View details'}
                      </span>
                    </div>
                  </button>
                  {isOpen ? (
                    <div className="account-orders-detail">
                      {detailLoading ? (
                        <p className="small text-muted mb-0">Loading details…</p>
                      ) : detailError ? (
                        <p className="small text-danger mb-0">{detailError}</p>
                      ) : detail && detail.order_number === o.order_number ? (
                        <>
                          {returnFeedback?.text ? (
                            <p
                              className={`small mb-2 account-orders-feedback account-orders-feedback--${returnFeedback.kind || 'success'}`}
                              role="status"
                            >
                              {returnFeedback.text}
                            </p>
                          ) : null}
                          <AccountOrderDetailBody
                            detail={detail}
                            onReorder={reorder}
                            onRequestCancel={requestCancel}
                            onOrderUpdated={setDetail}
                            setReturnFeedback={setReturnFeedback}
                          />
                        </>
                      ) : (
                        <p className="small text-muted mb-0">
                          Could not load order summary.{' '}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 align-baseline"
                            onClick={() => toggleOrder(o.order_number)}
                          >
                            Try again
                          </button>
                        </p>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
            )}
          </>
        )}
      </div>
  );

  if (embedded) return panel;

  return (
    <section className="account-password" aria-labelledby="account-orders-heading">
      <h2 id="account-orders-heading" className="account-section-label">
        Your orders
      </h2>
      {panel}
    </section>
  );
}
